import { Card } from '@chakra-ui/react';
import {
  IPriceUnderProductSchemaUI,
  IQuoteItemRespSchema,
  OfferingTypesEnum,
  PriceModelEnum,
  RateBillingFrequencyEnum,
} from '@monetize/types/app';
import { IOrderedObj, Maybe } from '@monetize/types/common';
import { formatNumber, sortByProductPosition } from '@monetize/utils/core';
import { FunctionComponent as FC, Fragment } from 'react';
import { OFFERINGS } from '../../../constants';
import { RATE_BILLING_FREQUENCY_MAP } from '../../../constants/offerings';
import { PRODUCT_TYPE_DISPLAY_SHORT } from '../../../constants/products';
import {
  MCard,
  MDivider,
  MFlex,
  MTable,
  MTbody,
  MTd,
  MText,
  MTh,
  MThead,
  MTr,
} from '../../Monetize';
import { MCurrency } from '../../Monetize/MCurrency';
import { MPercent } from '../../Monetize/MPercent';

interface PricesDrawerBodyViewProps {
  rateName: string;
  internalDescription?: Maybe<string>;
  offeringType: OfferingTypesEnum;
  productsWithPrices: IPriceUnderProductSchemaUI[];
  productsObj: IOrderedObj<IQuoteItemRespSchema>;
  currency: string;
  isCustomRate: boolean;
  billingFrequency?: RateBillingFrequencyEnum;
  billingFrequencyInMonths?: number | null;
}

export const PricesDrawerBodyView: FC<PricesDrawerBodyViewProps> = ({
  offeringType,
  internalDescription,
  rateName,
  productsWithPrices,
  productsObj,
  currency,
  isCustomRate,
  billingFrequency,
  billingFrequencyInMonths,
}: PricesDrawerBodyViewProps) => {
  const isPoTOfferingType =
    offeringType === OfferingTypesEnum.CUSTOM_PERCENT_OF_TOTAL;
  const sortedProductsWithPrices = sortByProductPosition(
    productsWithPrices.map((item) => ({
      ...item,
      position: productsObj.byId[item.id]?.position ?? 0,
    })),
  );

  return (
    <>
      <MCard variant="borderless" mb="2">
        <Card.Body justifyContent="space-between" alignItems={'center'}>
          <MText as="h5" fontSize="md" fontWeight="bold" truncate>
            {rateName}
          </MText>
          <MText mt="2" as="h5" fontSize="md" fontWeight="bold" lineClamp={1}>
            Subscription Frequency{' '}
            <MText as="span" fontWeight="normal">
              {billingFrequency
                ? RATE_BILLING_FREQUENCY_MAP[billingFrequency](
                    billingFrequencyInMonths,
                  ).label
                : ''}
            </MText>
          </MText>
          {isCustomRate && (
            <MText mt="2" fontSize="sm" fontStyle="italic" lineClamp={1}>
              This rate has been customized for this account.
            </MText>
          )}
          {internalDescription && (
            <MText mt="2" fontSize="sm" fontStyle="italic" lineClamp={1}>
              {internalDescription}
            </MText>
          )}
        </Card.Body>
      </MCard>
      <MCard variant="borderless" mt="2">
        <Card.Body>
          {sortedProductsWithPrices.map(
            ({ id, prices, name, productType }, index) => {
              const isLastRow = productsWithPrices.length - 1 === index;
              return (
                <Fragment key={id}>
                  <MFlex direction="column" pt="2">
                    <MText mb="4" as="h5" fontSize="md" fontWeight="bold">
                      {name}{' '}
                      <MText as="span" fontWeight="normal">
                        ({PRODUCT_TYPE_DISPLAY_SHORT[productType]})
                      </MText>
                    </MText>

                    <MTable size="sm">
                      <MThead>
                        <MTr _hover={{ bg: 'transparent' }}>
                          <MTh px={2} py={1} w="4" />
                          <MTh px={2} py={1} w="8.5rem">
                            Pricing Model
                          </MTh>
                          <MTh px={2} py={1} w="8.25rem">
                            From
                          </MTh>
                          <MTh px={2} py={1} w="8.25rem">
                            Up to
                          </MTh>
                          <MTh px={2} py={1} w="8.5rem">
                            {isPoTOfferingType ? 'Percentage' : 'Amount'}
                          </MTh>
                        </MTr>
                      </MThead>
                      <MTbody>
                        {prices.map(
                          (
                            { from, to, priceModel, amount, product },
                            productIndex: number,
                          ) => {
                            const formattedFrom = formatNumber(from, {
                              maximumFractionDigits: 0,
                            });

                            const formattedTo =
                              formatNumber(to, {
                                maximumFractionDigits: 0,
                              }) || '∞';

                            return (
                              <MTr key={`${productIndex}-range-${id}`}>
                                <MTd px={2} py={1} verticalAlign="top" w="4">
                                  <MFlex align="center" minH={8}>
                                    <MText color="tPurple.base">{`${
                                      productIndex + 1
                                    }.`}</MText>
                                  </MFlex>
                                </MTd>
                                <MTd px={2} py={1} verticalAlign="top">
                                  <MFlex align="center" minH="8">
                                    <MText>
                                      {
                                        OFFERINGS.PRICE_MODEL_OPTIONS[
                                          priceModel
                                        ]
                                      }
                                    </MText>
                                  </MFlex>
                                </MTd>
                                <MTd px={2} py={1} verticalAlign="top">
                                  <MFlex align="center" minH="8">
                                    <MText>{formattedFrom}</MText>
                                  </MFlex>
                                </MTd>
                                <MTd px={2} py={1} verticalAlign="top">
                                  <MFlex align="center" minH="8">
                                    <MText>{formattedTo}</MText>
                                  </MFlex>
                                </MTd>
                                <MTd px={2} py={1} verticalAlign="top">
                                  <MFlex align="center" minH="8">
                                    {isPoTOfferingType && (
                                      <MPercent amount={amount} />
                                    )}
                                    {!isPoTOfferingType && (
                                      <MCurrency
                                        amount={
                                          priceModel === PriceModelEnum.CUSTOM
                                            ? productsObj.byId[product.id]
                                                ?.unitPrice
                                            : amount
                                        }
                                        options={{
                                          maximumFractionDigits: 'max',
                                          currency,
                                        }}
                                      />
                                    )}
                                  </MFlex>
                                </MTd>
                              </MTr>
                            );
                          },
                        )}
                      </MTbody>
                    </MTable>
                  </MFlex>
                  {!isLastRow && <MDivider my="2" />}
                </Fragment>
              );
            },
          )}
        </Card.Body>
      </MCard>
    </>
  );
};
